import React from 'react'
import {Constants, Label, useContainerDimensionsContext} from '@therms/atalaya'
import './RecordFields.css'
import {RecordField} from './RecordField'
import {RPCDocs} from '@therms/react-modules'
import classNames from 'classnames'

interface PersonRecordFieldsProps {
  record: RPCDocs.Records.GetRecordProfile.v1.Record
}

function PersonRecordFields({record}: PersonRecordFieldsProps) {
  const {
    viewport: {width: mainWidth},
  } = useContainerDimensionsContext()

  let layout = 'Xxl'
  if (mainWidth < Constants.Breakpoint.Sm.width) {
    layout = 'Sm'
  } else if (mainWidth < Constants.Breakpoint.Xl.width) {
    layout = 'Xl'
  }

  return (
    <div className="grid grid-cols-6">
      <div
        className={classNames({
          'col-span-3': layout === 'Xxl',
          'col-span-6': layout !== 'Xxl',
        })}
      >
        <Label className="label">Name</Label>
        <RecordField fieldType="text" value={record.fields.name} />
      </div>

      <div
        className={classNames({
          'col-span-3': layout === 'Xxl',
          'col-span-6': layout !== 'Xxl',
        })}
      >
        <Label className="label">Alias / Nickname</Label>
        <RecordField value={record.fields.alias} />
      </div>

      <div
        className={classNames({
          'col-span-2': layout === 'Xxl',
          'col-span-3': layout === 'Xl',
          'col-span-6': layout === 'Sm',
        })}
      >
        <Label className="label">Race</Label>
        <RecordField fieldType="select" value={record.fields.race} />
      </div>

      <div
        className={classNames({
          'col-span-3': layout === 'Sm',
        })}
      >
        <Label className="label">Gender</Label>
        <RecordField fieldType="select" value={record.fields.gender} />
      </div>

      <div
        className={classNames({
          'col-span-3': layout === 'Sm',
        })}
      >
        <Label className="label">Height</Label>
        <RecordField fieldType="select" value={record.fields.height} />
      </div>

      <div
        className={classNames({
          'col-span-3': layout === 'Sm',
        })}
      >
        <Label className="label">Weight</Label>
        <RecordField fieldType="number" value={record.fields.weight} />
      </div>

      <div
        className={classNames({
          'col-span-3': layout === 'Sm',
        })}
      >
        <Label className="label">Eyes</Label>
        <RecordField fieldType="select" value={record.fields.eyes} />
      </div>

      <div
        className={classNames({
          'col-span-3': layout === 'Sm',
        })}
      >
        <Label className="label">Hair</Label>
        <RecordField fieldType="select" value={record.fields.hair} />
      </div>

      <div
        className={classNames({
          'col-span-3': layout === 'Sm',
        })}
      >
        <Label className="label">
          <span>
            <span>Age </span>
            <span className="text-color-subtle italic">
              {record.fields.approxAge ? 'approx' : 'exact'}
            </span>
          </span>
        </Label>

        <RecordField fieldType="number" value={record.fields.age} />
      </div>

      <div
        className={classNames({
          'col-span-2': layout === 'Xxl',
          'col-span-3': layout === 'Xl',
          'col-span-6': layout === 'Sm',
        })}
      >
        <Label className="label">DOB</Label>
        <RecordField fieldType="datetime" value={record.fields.dob} />
      </div>

      <div
        className={classNames({
          'col-span-2': layout === 'Xxl',
          'col-span-3': layout === 'Xl',
          'col-span-6': layout === 'Sm',
        })}
      >
        <Label className="label">SSN</Label>
        <RecordField value={record.fields.ss} />
      </div>

      <div
        className={classNames({
          'col-span-2': layout === 'Xxl',
          'col-span-3': layout === 'Xl',
          'col-span-6': layout === 'Sm',
        })}
      >
        <Label className="label">ID #</Label>
        <RecordField value={record.fields.idNumber} />
      </div>

      <div
        className={classNames({
          'col-span-2': layout === 'Xxl',
          'col-span-3': layout === 'Xl',
          'col-span-6': layout === 'Sm',
        })}
      >
        <Label className="label">ID State / Authority</Label>
        <RecordField value={record.fields.idAuthority} />
      </div>

      <div
        className={classNames({
          'col-span-2': layout === 'Xxl',
          'col-span-3': layout === 'Xl',
          'col-span-6': layout === 'Sm',
        })}
      >
        <Label className="label">Address</Label>
        <RecordField value={record.fields.streetAddress} />
      </div>

      <div
        className={classNames({
          'col-span-2': layout === 'Xxl',
          'col-span-3': layout === 'Xl',
          'col-span-6': layout === 'Sm',
        })}
      >
        <Label className="label">City</Label>
        <RecordField value={record.fields.locality} />
      </div>

      <div
        className={classNames({
          'col-span-2': layout === 'Xxl',
          'col-span-3': layout === 'Xl',
          'col-span-6': layout === 'Sm',
        })}
      >
        <Label className="label">State</Label>
        <RecordField value={record.fields.region} />
      </div>

      <div
        className={classNames({
          'col-span-2': layout === 'Xxl',
          'col-span-3': layout === 'Xl',
          'col-span-6': layout === 'Sm',
        })}
      >
        <Label className="label">Phone</Label>
        <RecordField value={record.fields.phone} />
      </div>

      <div
        className={classNames({
          'col-span-2': layout === 'Xxl',
          'col-span-3': layout === 'Xl',
          'col-span-6': layout === 'Sm',
        })}
      >
        <Label className="label">Email</Label>
        <RecordField value={record.fields.email} />
      </div>

      <div
        className={classNames({
          'col-span-2': layout === 'Xxl',
          'col-span-3': layout === 'Xl',
          'col-span-6': layout === 'Sm',
        })}
      >
        <Label className="label">Criminal Record #</Label>
        <RecordField value={record.fields.criminalId} />
      </div>

      <div
        className={classNames({
          'col-span-2': layout === 'Xxl',
          'col-span-3': layout === 'Xl',
          'col-span-6': layout === 'Sm',
        })}
      >
        <Label className="label">Criminal Authority / Agency</Label>
        <RecordField value={record.fields.criminalAuthority} />
      </div>

      <div className="col-span-6">
        <Label className="label">Additional Info</Label>
        <RecordField fieldType="textarea" value={record.fields.additional} />
      </div>
    </div>
  )
}

export {PersonRecordFields}
